import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ShieldCheck,
  Wallet,
  ArrowRight,
  ExternalLink,
  Loader2,
  AlertTriangle,
  Lock,
  Fingerprint,
} from "lucide-react";

import { api } from "@/lib/api";
import AutheraLogo from "@/components/AutheraLogo";

function formatExpiry(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export default function Claim() {
  const { claimToken } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get(`/public/claim/${claimToken}`);
        if (!cancelled) setData(data);
      } catch {
        if (!cancelled)
          setData({ valid: false, reason: "Failed to load claim details." });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [claimToken]);

  if (loading) {
    return (
      <div
        data-testid="claim-loading"
        className="min-h-[60vh] flex items-center justify-center text-sm text-gray-500"
      >
        <Loader2 size={16} className="animate-spin mr-2" />
        Loading claim…
      </div>
    );
  }

  if (!data?.valid) {
    return (
      <div
        data-testid="claim-invalid"
        className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-16"
      >
        <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_12px_32px_rgba(0,0,0,0.05)] overflow-hidden">
          <div className="h-[3px] bg-gradient-to-r from-transparent via-red-500 to-transparent" />
          <div className="px-7 py-9 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-red-50 text-red-600 flex items-center justify-center">
              <AlertTriangle size={20} />
            </div>
            <h1 className="font-display font-bold text-gray-900 text-2xl mt-4">
              Claim unavailable
            </h1>
            <p className="mt-2 text-sm text-gray-600">
              {data?.reason ||
                "This claim link could not be verified. Please request a new one from the certificate owner."}
            </p>
            <Link
              to="/verify"
              data-testid="claim-go-verify"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0055FF] hover:underline"
            >
              Verify a certificate instead
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Wallet-connect SCAFFOLD only — feature explicitly deferred per product
  // brief. Clicking the button shows a "coming soon" notice rather than
  // pretending to connect a wallet that we can't yet sign through.
  const onConnect = () => {
    setConnecting(true);
    setTimeout(() => setConnecting(false), 1100);
  };

  return (
    <div
      data-testid="claim-page"
      className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
    >
      <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_24px_64px_rgba(0,0,0,0.08)] overflow-hidden">
        <div className="h-[3px] bg-gradient-to-r from-transparent via-[#0055FF] to-transparent" />

        {/* Brand */}
        <div className="pt-10 pb-2 flex justify-center">
          <AutheraLogo size={64} glow />
        </div>

        {/* Pill */}
        <div className="text-center">
          <span className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white px-3 py-1 rounded-full text-[11px] font-bold tracking-wide">
            <ShieldCheck size={12} strokeWidth={2.5} />
            CLAIM YOUR CERTIFICATE
          </span>
        </div>

        {/* Header */}
        <div className="px-8 sm:px-12 pt-6 pb-4 text-center">
          <h1 className="font-display font-bold tracking-tight text-gray-900 text-3xl sm:text-4xl">
            Take Full Ownership
          </h1>
          <p className="mt-3 text-[15px] text-gray-600 max-w-lg mx-auto leading-relaxed">
            Your certificate is currently secured in an Authera Wallet. Connect
            your personal wallet to take full ownership.
          </p>
        </div>

        {/* Asset summary */}
        <div className="px-8 sm:px-12">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 grid sm:grid-cols-2 gap-4">
            <div>
              <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                Item
              </div>
              <div
                data-testid="claim-product-name"
                className="mt-1 font-display font-semibold text-gray-900"
              >
                {data.product_name}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                Retailer
              </div>
              <div className="mt-1 text-sm text-gray-900">{data.retailer}</div>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                Certificate ID
              </div>
              <div
                data-testid="claim-cert-id"
                className="mt-1 font-mono text-xs text-gray-900"
              >
                {data.certificate_id || "—"}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-gray-500">
                Network
              </div>
              <div className="mt-1 text-sm text-gray-900">
                {data.network || "—"}
              </div>
            </div>
          </div>
        </div>

        {/* Connect CTA — scaffold (no real wallet provider yet) */}
        <div className="px-8 sm:px-12 pt-7 pb-4 text-center">
          <button
            type="button"
            onClick={onConnect}
            disabled={connecting}
            data-testid="claim-connect-wallet"
            className="inline-flex items-center justify-center gap-2 bg-[#0055FF] text-white text-sm font-semibold px-5 py-3 rounded-md hover:bg-[#0044CC] transition disabled:opacity-60"
          >
            {connecting ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />}
            {connecting ? "Connecting…" : "Connect Wallet"}
          </button>
          <div
            data-testid="claim-coming-soon"
            className="mt-3 text-[12px] text-gray-500 max-w-md mx-auto leading-relaxed"
          >
            On-chain ownership transfer is rolling out shortly. Save this link —
            it stays valid until{" "}
            <span className="text-gray-900 font-medium">
              {formatExpiry(data.claim_expires_at)}
            </span>
            .
          </div>
        </div>

        {/* Trust strip */}
        <div className="px-8 sm:px-12 pb-10 mt-3">
          <div className="bg-white border border-gray-200 rounded-xl p-5 grid sm:grid-cols-3 gap-5">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-blue-50 text-[#0055FF] flex items-center justify-center">
                <Lock size={15} strokeWidth={2.2} />
              </span>
              <div>
                <div className="font-semibold text-gray-900 text-sm">
                  Secure handover
                </div>
                <div className="text-xs text-gray-500 leading-relaxed">
                  You control your wallet. Authera never holds your keys.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-blue-50 text-[#0055FF] flex items-center justify-center">
                <Fingerprint size={15} strokeWidth={2.2} />
              </span>
              <div>
                <div className="font-semibold text-gray-900 text-sm">
                  Tamper-evident
                </div>
                <div className="text-xs text-gray-500 leading-relaxed">
                  Cryptographic signatures bind ownership to your wallet.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="mt-0.5 h-8 w-8 shrink-0 rounded-lg bg-blue-50 text-[#0055FF] flex items-center justify-center">
                <ShieldCheck size={15} strokeWidth={2.2} />
              </span>
              <div>
                <div className="font-semibold text-gray-900 text-sm">
                  Verifiable forever
                </div>
                <div className="text-xs text-gray-500 leading-relaxed">
                  Anyone can re-verify authenticity from the public ledger.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 bg-gray-50/70 px-8 sm:px-12 py-6 text-center">
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-gray-700">
            Verified by Authera
          </div>
          <div className="text-[11px] text-gray-500 mt-1">
            Digital Asset Verification Layer
          </div>
          <div className="mt-3">
            <Link
              to={`/verify/${data.tx_hash || ""}`}
              data-testid="claim-back-to-verify"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0055FF] hover:underline"
            >
              View public verification page
              <ExternalLink size={12} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
